// Derived stats. Every "effective" number the game reasons about (harvest
// damage, settlement defense, production rates, multipliers) is computed here
// from the raw state plus run modifiers plus permanent perks. Systems and the
// HUD both read these, so the numbers the player sees always match the numbers
// the simulation uses.

import type { GameState } from '@core/state';
import { BUILDINGS, BUILDING_IDS, type BuildingId } from '@data/buildings';
import { RESOURCE_ORDER, type ResourceId } from '@data/resources';
import { BALANCE } from '@data/balance';
import { raiderThreat, gateBossThreat } from '@data/enemies';
import { planetThreatScale } from '@data/planets';

/** Sum of r^0 + r^1 + ... + r^(level-1). Output of a building scales by this:
 *  each level adds 1.1x the previous level's contribution, so a building's
 *  total output accelerates while its price (x1.15/level) outpaces it. */
export function geometricSum(growth: number, level: number): number {
  if (level <= 0) return 0;
  if (growth === 1) return level;
  return (Math.pow(growth, level) - 1) / (growth - 1);
}

function level(state: GameState, id: BuildingId): number {
  return state.run.buildings[id] ?? 0;
}

/** Economy axis: a gentle linear bonus to the global production multiplier. */
export function economyAxis(state: GameState): number {
  let e = 0;
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    if (def.economyPerLevel) e += def.economyPerLevel * level(state, id);
  }
  return e;
}

/** Knowledge axis: linear tech points feeding the defense tech factor. */
export function knowledgeAxis(state: GameState): number {
  let k = 0;
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    if (def.knowledgePerLevel) k += def.knowledgePerLevel * level(state, id);
  }
  return k;
}

/** Raw military power: geometric defense so it can keep pace with wave threat. */
export function militaryPower(state: GameState): number {
  let m = 0;
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    if (def.defensePerLevel) m += def.defensePerLevel * geometricSum(BALANCE.productionGrowth, level(state, id));
  }
  return m;
}

/** Population cap from housing (linear). */
export function populationCap(state: GameState): number {
  let cap = BALANCE.population.capBase;
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    if (def.popCapPerLevel) cap += def.popCapPerLevel * level(state, id);
  }
  return cap;
}

/** Product of all active timed buffs' production multipliers (Surge tech). */
export function buffMultiplier(state: GameState): number {
  let m = 1;
  for (const b of state.run.buffs) m *= b.prodMult;
  return m;
}

/** Permanent global production multiplier from accumulated cosmic currency. */
export function cosmicProductionMult(state: GameState): number {
  return 1 + state.meta.cosmic * BALANCE.planets.cosmicProductionPer;
}

/** Permanent global defense multiplier from accumulated cosmic currency. */
export function cosmicDefenseMult(state: GameState): number {
  return 1 + state.meta.cosmic * BALANCE.planets.cosmicDefensePer;
}

/** Global production multiplier applied to all resource income. */
export function productionMultiplier(state: GameState): number {
  return (
    (1 + economyAxis(state)) *
    state.run.modifiers.productionMult *
    state.metaPerks.globalProductionMult *
    buffMultiplier(state) *
    cosmicProductionMult(state)
  );
}

/** Combined knowledge multiplier from run modifiers and permanent perks. */
export function knowledgeMultiplier(state: GameState): number {
  return state.run.modifiers.knowledgeMult * state.metaPerks.globalKnowledgeMult;
}

/** How much the settlement's SIZE inflates incoming wave threat, so a compounding
 *  defense cannot permanently outscale the age curve. Age is the base threat axis
 *  now, so only building count taxes here (past an onboarding-free window). */
export function developmentThreatMult(state: GameState): number {
  const w = BALANCE.waves;
  const buildings = Math.max(0, state.run.buildingCount - w.threatFreeBuildings);
  return 1 + buildings * w.threatPerBuilding;
}

/** The threat the settlement actually faces on the current wave: the age-driven
 *  raider band (or the era gate boss, when asBoss) scaled by settlement size and
 *  planet difficulty. Defaults to the latched combatIsBoss so a resolving wave
 *  and its telegraph banner always agree; pass gatePending for a next-wave preview. */
export function effectiveThreat(state: GameState, asBoss = state.run.combatIsBoss): number {
  const run = state.run;
  const base = asBoss ? gateBossThreat(run.age) : raiderThreat(run.age, run.wavesThisAge);
  // Feed the softener RAIDERS cleared only: run.wave also counts gate-boss wins
  // (one per boss-gated advance = age - firstGateAge), which must not consume
  // the raider onboarding window.
  const raidersCleared = run.wave - Math.max(0, run.age - BALANCE.ages.firstGateAge);
  const onboard = asBoss ? 1 : onboardThreatMult(raidersCleared);
  return base * onboard * developmentThreatMult(state) * planetThreatScale(state.meta.planet);
}

/** Onboarding softener for the first raider waves: fed the count of RAIDERS
 *  cleared (gate bosses excluded), so facing raider 1 it is 0 (fullest
 *  softening) and it lerps to 1.0 by `onboardWaves`. Keeps a new player's
 *  economy/knowledge opening pick from being an instant wave-1 death. Never
 *  applied to gate bosses; a no-op past the window. */
function onboardThreatMult(wavesCleared: number): number {
  const w = BALANCE.waves;
  if (wavesCleared >= w.onboardWaves) return 1;
  const t = Math.max(0, wavesCleared) / w.onboardWaves; // 0 at wave 1, ->1 by the Nth
  return w.onboardThreatMult + (1 - w.onboardThreatMult) * t;
}

/** Final settlement defense compared against wave threat. The knowledge tech
 *  factor uses sqrt of the (unbounded) knowledge axis for diminishing returns,
 *  so it strengthens defense early without exploding into a late-game runaway. */
export function settlementDefense(state: GameState): number {
  const tech =
    1 +
    Math.sqrt(knowledgeAxis(state)) * BALANCE.defense.knowledgeTechFactor * knowledgeMultiplier(state);
  const raw = BALANCE.defense.base + militaryPower(state) + state.run.modifiers.defenseFlat;
  return (
    raw * tech * state.run.modifiers.defenseMult * state.metaPerks.defenseMult * cosmicDefenseMult(state)
  );
}

export function harvestDamage(state: GameState): number {
  return (
    BALANCE.harvest.baseDamage *
    state.run.modifiers.harvestDamageMult *
    state.metaPerks.harvestDamageMult
  );
}

export function harvestRadius(state: GameState): number {
  return (
    BALANCE.harvest.baseRadius *
    state.run.modifiers.harvestRadiusMult *
    state.metaPerks.harvestRadiusMult
  );
}

/** Workforce assigned to gathering (all population, for the MVP). */
export function autoWorkers(state: GameState): number {
  return state.run.autoUnlocked ? state.run.population : 0;
}

/** Auto-collection income per basic resource, before the global multiplier.
 *  Split across the three basics so the settlement gathers a believable mix. */
const AUTO_SPLIT: Partial<Record<ResourceId, number>> = { wood: 0.4, stone: 0.35, ore: 0.25 };

/** Per-second auto-collection rate per basic resource (workforce portion only,
 *  fully multiplied). The courier animation reads this so a worker's load over a
 *  round trip equals the real income it represents. Zero when auto is locked. */
export function autoCollectRates(state: GameState): Record<'wood' | 'stone' | 'ore', number> {
  const out = { wood: 0, stone: 0, ore: 0 };
  const workers = autoWorkers(state);
  if (workers <= 0) return out;
  const per =
    workers *
    BALANCE.autoCollect.basePerWorker *
    state.run.modifiers.autoCollectMult *
    state.metaPerks.autoCollectMult *
    productionMultiplier(state);
  out.wood = per * (AUTO_SPLIT.wood ?? 0);
  out.stone = per * (AUTO_SPLIT.stone ?? 0);
  out.ore = per * (AUTO_SPLIT.ore ?? 0);
  return out;
}

/**
 * Per-second income for every resource: building production plus auto-collect,
 * scaled by the global production multiplier (knowledge also by knowledgeMult).
 * This single function feeds both the production tick and the HUD rate display.
 */
export function computeIncome(state: GameState): Record<ResourceId, number> {
  const income = {} as Record<ResourceId, number>;
  for (const id of RESOURCE_ORDER) income[id] = 0;

  const prodMult = productionMultiplier(state);

  // Building production (geometric per level).
  for (const id of BUILDING_IDS) {
    const def = BUILDINGS[id];
    if (!def.produce) continue;
    const lvl = level(state, id);
    if (lvl <= 0) continue;
    const base = def.produce.perLevel * geometricSum(BALANCE.productionGrowth, lvl);
    income[def.produce.resource] += base;
  }

  // Auto-collection of basics from workforce.
  const workers = autoWorkers(state);
  if (workers > 0) {
    const perResource =
      workers * BALANCE.autoCollect.basePerWorker * state.run.modifiers.autoCollectMult * state.metaPerks.autoCollectMult;
    for (const [res, share] of Object.entries(AUTO_SPLIT) as [ResourceId, number][]) {
      income[res] += perResource * share;
    }
  }

  // Apply the global production multiplier (knowledge gets its own factor too).
  for (const id of RESOURCE_ORDER) {
    income[id] *= prodMult;
  }
  income.knowledge *= knowledgeMultiplier(state);

  return income;
}

/** Weighted progress total used for age thresholds (deeper resources count
 *  more). Mirrors the comment in balance.ts (ages.baseThreshold). */
export function progressValue(amounts: Partial<Record<ResourceId, number>>): number {
  return (
    (amounts.wood ?? 0) +
    (amounts.stone ?? 0) +
    (amounts.ore ?? 0) * 1.5 +
    (amounts.knowledge ?? 0) * 3
  );
}
